import { Helmet } from 'react-helmet-async';
import { getResizedImageUrl } from '@flame-frontend-utils/tiptap';
import { MediaPostQuery } from '../gql/MediaPost.document';
import { getDisplayedName } from '../../../lib/getDisplayedName';
import { PostSpecies } from '../../../lib/graphql.document';
import { ROUTES } from '../../../lib/ROUTES';
import { MAX_DIMENSIONS, CoverType, isCropSettingsMap } from '../../../components/CoverImage';
import { formatInAppTimezone } from '../../../lib/date-fns';

interface LinkedDataProps {
  post: Exclude<MediaPostQuery['mediaPost'], null | undefined>;
}

const LINKED_DATA_POST_CLASS_NAME = 'linked-data-post';

const LinkedData = ({ post }: LinkedDataProps) => {
  const author = post.authors[0];

  const image = post.cover?.originalUrl
    ? {
        '@type': 'ImageObject',
        url: getResizedImageUrl({
          resizeOrigin: PUBLIC_CONFIG.IMAGE_RESIZE_ORIGIN,
          originalUrl: post.cover.originalUrl,
          cropSettings: isCropSettingsMap(post.cover.cropSettings) ? post.cover.cropSettings.horizontal : undefined,
          width: MAX_DIMENSIONS[CoverType.Promo].width,
          height: MAX_DIMENSIONS[CoverType.Promo].height,
          dpr: 2,
        }),
        width: MAX_DIMENSIONS[CoverType.Promo].width * 2,
        height: MAX_DIMENSIONS[CoverType.Promo].height * 2,
      }
    : undefined;

  const linkedData =
    post.species === PostSpecies.Blog
      ? {
          '@context': 'https://schema.org',
          '@type': 'DiscussionForumPosting',
          headline: post.shortTitle || post.title,
          articleBody: truncateText(post.bodyPlaintext ?? ''),
          inLanguage: 'ru',
          isAccessibleForFree: true,
          discussionUrl: `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.POST.$buildPath({ params: { slug: post.slug } })}`,
          datePublished: toIsoWithAppTimezone(post.publishAt),
          dateModified: toIsoWithAppTimezone(post.publishAt),
          image,
          author: author
            ? {
                '@type': 'Person',
                name: getDisplayedName(author.nickname, author.alias),
                url: `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.USER_FILTER.$buildPath({ params: { id: author.alias } })}`,
              }
            : undefined,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.POST.$buildPath({ params: { slug: post.slug } })}`,
          },
          commentCount: post.commentsCount,
          publisher: {
            '@type': 'Organization',
            name: PUBLIC_CONFIG.APP_NAME,
            url: PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST,
            logo: {
              '@type': 'ImageObject',
              url: `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}/android-chrome-512x512.png`,
              width: 512,
              height: 512,
            },
          },
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          '@id': `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.POST.$buildPath({ params: { slug: post.slug } })}`,
          url: `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.POST.$buildPath({ params: { slug: post.slug } })}`,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.POST.$buildPath({ params: { slug: post.slug } })}`,
          },
          headline: post.shortTitle || post.title,
          description: post.description,
          inLanguage: 'ru',
          keywords: post.tags.map((tag) => tag.value),
          about: post.tags.map((tag) => ({ '@type': 'Thing', name: tag.value })),
          datePublished: toIsoWithAppTimezone(post.publishAt),
          dateModified: toIsoWithAppTimezone(post.publishAt),
          isAccessibleForFree: true,
          image,
          thumbnailUrl: image?.url,
          author: author
            ? {
                '@type': 'Person',
                name: getDisplayedName(author.nickname, author.alias),
                url: `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}${ROUTES.USER_FILTER.$buildPath({ params: { id: author.alias } })}`,
              }
            : undefined,
          publisher: {
            '@type': 'Organization',
            name: PUBLIC_CONFIG.APP_NAME,
            url: PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST,
            logo: {
              '@type': 'ImageObject',
              url: `${PUBLIC_CONFIG.CANONICAL_ROBOTS_HOST}/android-chrome-512x512.png`,
              width: 512,
              height: 512,
            },
          },
          copyrightHolder: {
            '@type': 'Organization',
            name: PUBLIC_CONFIG.APP_NAME,
          },
          articleBody: post.bodyPlaintext ?? '', // Full text!
        };

  return (
    <Helmet
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(linkedData),
        },
      ]}
    />
  );
};

function truncateText(text: string, maxLength = 500) {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 3)}...`;
}

function toIsoWithAppTimezone(date: string | number) {
  return formatInAppTimezone(date, "yyyy-MM-dd'T'HH:mm:ssXXX");
}

export { LinkedData, LINKED_DATA_POST_CLASS_NAME };
